import { html, nothing } from "lit";
import { ref } from "lit/directives/ref.js";
import {
  FLEET_SEND_OPTIONS_HINTS,
  FLEET_CHAT_EDIT_HINTS,
  FLEET_CHAT_FOCUS_HINTS,
} from "../control-hints.ts";
import { icons } from "../icons.ts";
import type { ChatAttachment } from "../ui-types.ts";
import { resolveLiveThinkingSummary } from "./chat-activity-collect.ts";
import { resolveAssistDisplay } from "./chat-assist-display.ts";
import { renderAttachmentPreview } from "./chat-composer-attachments.ts";
import {
  appendMentionOrSlashTrigger,
  flushComposeDraftFromDom,
  readComposeTextareaValue,
  stripTrailingSlashOrMentionToken,
  syncChatComposeDraft,
} from "./chat-composer-draft.ts";
import { renderChatComposerFileReview } from "./chat-composer-file-review.ts";
import { renderComposerLiveStep, resolveComposerLiveStep } from "./chat-composer-live-step.ts";
import { renderChatComposerPlusMenu } from "./chat-composer-plus-menu.ts";
import { hasActiveToolInStream } from "./chat-live-run.ts";
import { sessionChildren } from "./chat-session-children.ts";
import { renderChatSyncPill } from "./chat-sync-pill.ts";
import type { ChatProps, CompactionIndicatorStatus } from "./chat-view-props.ts";
import { formatChatWaitElapsed, shouldShowLiveRunIndicator } from "./chat-wait-elapsed.ts";
import {
  applyComposerLocalSlashCommand,
  applyComposerLocalSlashFromDraft,
  listComposerSlashCommands,
  shouldSendAfterSlashPick,
} from "./composer-local-slash.ts";
import { composerBlockCalloutClass } from "./composer-reason-channel.ts";
import { renderContextUsageRing } from "./context-usage-ring.ts";
import { messageTextContent } from "./fleet-chat-send-options.ts";
import { projectsForChatContext } from "./fleet-projects-session.ts";
import { selfImprovementWorkspaceHint } from "./fleet-self-improvement-workspace.ts";
import {
  previewFleetSendRouting,
  renderFleetSendAckBanner,
  renderFleetSendRoutingPreview,
} from "./fleet-send-routing-preview.ts";
import {
  extractSlashCommandQuery,
  filterSlashCommands,
  formatSlashCommandDraft,
} from "./fleet-slash-commands.ts";
import { extractMentionQuery, filterAgents, renderMentionPicker } from "./mention-picker.ts";
import { extractThinking } from "./message-extract.ts";
import { detectMultiAgentIntent } from "./multi-agent-hint.ts";
import { renderSlashCommandPicker } from "./slash-command-picker.ts";

const COMPACTION_TOAST_DURATION_MS = 5000;

/** Legacy multi-agent template picker removed with `src/multi-agent/`. */
export function renderMultiagentTemplatePicker(_props: ChatProps) {
  return nothing;
}

/**
 * UX-PLAN Pillar F — render the composer's typed reason channel.
 *
 * Renders one `.callout` per `ComposerBlock`. The legacy
 * `disabledReason: string` is preserved as a single legacy callout so
 * callers that have not been migrated still get a signal (the gate
 * stays closed, the message stays visible).
 */
export function renderComposerReasonChannel(props: ChatProps) {
  const blocks = props.composerBlocks ?? [];
  const legacy = props.disabledReason;
  if (blocks.length === 0 && !legacy) {
    return nothing;
  }
  const blockCallouts = blocks.map((block, index) => {
    const modifier = composerBlockCalloutClass(block);
    const testId = `chat-composer-reason-${block.kind}`;
    return html`
      <div
        class="callout ${modifier} chat-composer-reason"
        data-testid=${testId}
        role=${block.kind === "offline" ? "alert" : "status"}
        aria-live=${block.kind === "offline" ? "assertive" : "polite"}
        data-kind=${block.kind}
        data-block-index=${index}
      >
        <div class="chat-composer-reason__message">${block.message}</div>
        ${
          block.recover
            ? html`<div class="chat-composer-reason__recover">${block.recover}</div>`
            : nothing
        }
      </div>
    `;
  });
  if (legacy) {
    blockCallouts.push(html`
      <div
        class="callout danger chat-composer-reason"
        data-testid="chat-composer-reason-legacy"
        role="alert"
        aria-live="assertive"
        data-kind="legacy"
      >
        <div class="chat-composer-reason__message">${legacy}</div>
      </div>
    `);
  }
  return html`
    <div class="chat-composer-reason-stack" data-testid="chat-composer-reason-stack">
      ${blockCallouts}
    </div>
  `;
}

export function renderFleetSendOptions(props: ChatProps, opts?: { embedded?: boolean }) {
  const composerMode = props.composerMode ?? "agent";
  if (composerMode !== "agent") {
    return nothing;
  }
  const orchestrate = props.chatSendOrchestrateAll ?? false;
  const createNewProject = props.chatSendCreateNewProject ?? false;
  const selfImprovement = props.chatSendSelfImprovement ?? false;
  const sendProjectId = props.chatSendProjectId ?? "";
  const sessionProjects = projectsForChatContext(props.fleetProjects ?? [], props.sessionKey);
  const suggest = detectMultiAgentIntent(props.draft);
  const selfImprovementHint = selfImprovementWorkspaceHint({
    selfImprovement,
    repoWorkspace: props.chatSelfImprovementRepoWorkspace,
    sessionWorkspace: props.chatSessionWorkspacePath,
  });
  const embedded = opts?.embedded === true;

  return html`
    <div
      class="chat-compose__send-options ${suggest ? "chat-compose__send-options--suggested" : ""} ${embedded ? "chat-compose__send-options--embedded" : ""}"
      role="group"
      aria-label="Send options"
    >
      <p class="chat-compose__send-options-intro muted">${FLEET_SEND_OPTIONS_HINTS.groupIntro}</p>
      <label class="chat-compose__send-option" title=${FLEET_SEND_OPTIONS_HINTS.orchestrate}>
        <input
          type="checkbox"
          data-testid="chat-send-orchestrate"
          .checked=${orchestrate}
          ?disabled=${!props.connected || selfImprovement}
          @change=${(e: Event) =>
            props.onChatSendOrchestrateAllChange?.((e.target as HTMLInputElement).checked)}
        />
        <span>Orchestrate with @all</span>
      </label>
      <label class="chat-compose__send-option" title=${FLEET_SEND_OPTIONS_HINTS.createNewProject}>
        <input
          type="checkbox"
          .checked=${createNewProject}
          ?disabled=${!props.connected || Boolean(sendProjectId)}
          @change=${(e: Event) =>
            props.onChatSendCreateNewProjectChange?.((e.target as HTMLInputElement).checked)}
        />
        <span>Create new project</span>
      </label>
      <label class="chat-compose__send-option" title=${FLEET_SEND_OPTIONS_HINTS.selfImprovement}>
        <input
          type="checkbox"
          .checked=${selfImprovement}
          ?disabled=${!props.connected || orchestrate}
          @change=${(e: Event) =>
            props.onChatSendSelfImprovementChange?.((e.target as HTMLInputElement).checked)}
        />
        <span>Self improvement</span>
      </label>
      <label
        class="chat-compose__send-option chat-compose__send-option--project"
        title=${FLEET_SEND_OPTIONS_HINTS.useProject}
      >
        <span class="chat-compose__send-option-label">Project</span>
        <select
          class="chat-compose__project-select"
          .value=${sendProjectId}
          ?disabled=${!props.connected || createNewProject || sessionProjects.length === 0}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            props.onChatSendProjectIdChange?.(value ? value : null);
          }}
        >
          <option value="">— none —</option>
          ${sessionProjects.map(
            (p) => html`
              <option value=${p.id} ?selected=${sendProjectId === p.id}>${p.name}</option>
            `,
          )}
        </select>
      </label>
      ${
        selfImprovementHint
          ? html`
              <p class="chat-compose__self-improvement-hint muted" role="note">
                ${selfImprovementHint}
              </p>
            `
          : nothing
      }
    </div>
  `;
}

/** Dock tools: one-click @all + Controls opener (panel lives in the grow rail). */
export function renderFleetComposeControlsDock(props: ChatProps) {
  if (!props.onChatSendOrchestrateAllChange) {
    return nothing;
  }
  if ((props.composerMode ?? "agent") !== "agent") {
    return nothing;
  }
  // Orchestrate moved out of the dock: it's no longer a one-click toggle
  // next to Attach/Palace/Draft. The composer defaults to orchestrate=off
  // and the orchestrating agent decides per workload. Users can still
  // flip it from the Controls dropdown (rendered in the grow rail).
  const selfImprovement = props.chatSendSelfImprovement ?? false;
  const controlsActive =
    (props.chatSendCreateNewProject ?? false) ||
    selfImprovement ||
    Boolean(props.chatSendProjectId);

  return html`
    <div class="chat-compose__controls-wrap">
      <button
        type="button"
        class="chat-compose__tool chat-compose__controls-btn ${controlsActive ? "chat-compose__controls-btn--active" : ""}"
        data-testid="chat-composer-controls"
        title="Send controls: @all, project, and self-improvement"
        aria-label="Open send controls"
        aria-haspopup="dialog"
        ?disabled=${!props.connected}
        @click=${(e: MouseEvent) => {
          const dock = (e.currentTarget as HTMLElement).closest(
            "[data-testid='chat-composer-dock']",
          );
          const dropdown = dock?.querySelector(".chat-compose__controls-dropdown");
          const wasHidden = dropdown?.classList.contains("hidden") !== false;
          document
            .querySelectorAll(".chat-compose__controls-dropdown")
            .forEach((el) => el.classList.add("hidden"));
          document
            .querySelectorAll(".chat-compose__multiagent-dropdown")
            .forEach((el) => el.classList.add("hidden"));
          props.onCloseChatPalaceContinuity?.();
          const draft = dock?.querySelector("fleet-assist-draft") as
            | (HTMLElement & { close?: () => void })
            | null;
          draft?.close?.();
          if (wasHidden && dropdown) {
            dropdown.classList.remove("hidden");
          }
        }}
      >
        ${icons.settings} Controls
      </button>
    </div>
  `;
}

export function renderFleetComposeControlsGrowPanel(props: ChatProps) {
  if (!props.onChatSendOrchestrateAllChange) {
    return nothing;
  }
  if ((props.composerMode ?? "agent") !== "agent") {
    return nothing;
  }
  return html`
    <div
      class="chat-compose__controls-dropdown chat-compose__grow-panel hidden"
      role="dialog"
      aria-label="Send controls"
      data-testid="chat-composer-controls-panel"
    >
      <div class="chat-compose__controls-dropdown-header">
        <span>Send controls</span>
        <button
          type="button"
          class="btn btn--sm btn--icon"
          title="Close"
          aria-label="Close send controls"
          @click=${() => {
            document
              .querySelectorAll(".chat-compose__controls-dropdown")
              .forEach((el) => el.classList.add("hidden"));
          }}
        >
          ${icons.x}
        </button>
      </div>
      ${renderFleetSendOptions(props, { embedded: true })}
    </div>
  `;
}
const COMPOSER_STICKY_MAX_ENTRIES = 32;
const composerStickyBySession = new Map<string, string | null>();

/**
 * JAISIU-3752 — parallel per-session memo for the live thinking
 * summary that drives the composer footer pill. Same LRU bound as
 * `composerStickyBySession` so the two stickies share the same
 * retention window. Tracked independently so a liveRunHint change
 * does not bump the thinking summary sticky and vice versa.
 */
const composerStickyThinkingBySession = new Map<string, string | null>();

function rememberComposerSticky(sessionKey: string, hint: string | null): void {
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) {
    return;
  }
  if (composerStickyBySession.has(trimmedKey)) {
    composerStickyBySession.delete(trimmedKey);
  }
  composerStickyBySession.set(trimmedKey, hint);
  while (composerStickyBySession.size > COMPOSER_STICKY_MAX_ENTRIES) {
    const oldest = composerStickyBySession.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    composerStickyBySession.delete(oldest);
  }
}

function rememberComposerStickyThinking(sessionKey: string, summary: string | null): void {
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) {
    return;
  }
  if (composerStickyThinkingBySession.has(trimmedKey)) {
    composerStickyThinkingBySession.delete(trimmedKey);
  }
  composerStickyThinkingBySession.set(trimmedKey, summary);
  while (composerStickyThinkingBySession.size > COMPOSER_STICKY_MAX_ENTRIES) {
    const oldest = composerStickyThinkingBySession.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    composerStickyThinkingBySession.delete(oldest);
  }
}

function clearComposerStickyForSession(sessionKey: string): void {
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) {
    return;
  }
  composerStickyBySession.delete(trimmedKey);
  composerStickyThinkingBySession.delete(trimmedKey);
}

function getComposerStickyForSession(sessionKey: string): string | null {
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) {
    return null;
  }
  return composerStickyBySession.get(trimmedKey) ?? null;
}

function getComposerStickyThinkingForSession(sessionKey: string): string | null {
  const trimmedKey = sessionKey.trim();
  if (!trimmedKey) {
    return null;
  }
  return composerStickyThinkingBySession.get(trimmedKey) ?? null;
}

/**
 * JAISIU-composer-live-step-indicator — render the live-step pill when
 * the model is actively doing something, else fall back to the existing
 * `renderChatSyncPill` (Awaiting reply / Syncing / Disconnected). One
 * render path keeps the two surfaces decoupled.
 */
/**
 * Unified composer status slot — single render path for the dock status
 * indicator (was split between `renderComposerLiveStepOnly` in the tools
 * row and `renderComposerFooterPill` under the dock).
 *
 * Priority:
 *   1. Live step (Thinking… / Running / etc.) when the agent is busy.
 *   2. Queued pill when there are queued messages but the agent is idle.
 *   3. Sync pill (Awaiting reply / Syncing / Disconnected) when stale.
 *   4. `nothing` — no gap reserved.
 *
 * Returns one indicator in the same DOM slot (the tools row after the
 * heartbeat / context chip), so the operator reads status at a single
 * glance.
 */

/**
 * JAISIU-3752 — derive the composer footer pill's thinking summary
 * from the same fields the Inspector activity panel uses. The
 * `lastAssistantThinking` fallback iterates the chat history in reverse
 * so a transient clear of the live reasoning stream (tool boundary,
 * model re-anchor) keeps the operator reading the same model message
 * they were tracking — the same contract as the live bubble.
 */
export function resolveComposerThinkingSummary(props: ChatProps): string {
  let lastAssistantThinking: string | null = null;
  const messages = props.messages ?? [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const t = extractThinking(messages[i]);
    if (t && t.trim()) {
      lastAssistantThinking = t;
      break;
    }
  }
  // `stream` is deliberately NOT passed here.
  //
  // `stream` is the model's *final answer text*. Feeding it into the
  // thinking summary made the composer's status pill render the first
  // non-empty line of the answer verbatim, inside a
  // `role="status" aria-live="polite"` element — so an ordinary assistant
  // sentence was announced and displayed as though the system had emitted
  // it. That is how a line like "Definitive evidence the tunnel is dead."
  // ended up in the composer: it was never a tunnel message at all, it was
  // the model's prose wearing a system-status label.
  //
  // The remaining sources are the model's own working notes
  // (`reasoningStream`) and the last extracted thinking block, which are
  // what this pill is for. The live assistant bubble still renders `stream`
  // normally, so the operator does not lose the text — it is simply no
  // longer mislabelled as system status. With both removed the caller
  // falls through to the operational phase label ("Running tools…",
  // "Reading file…"), which is the correct content for this slot.
  return resolveLiveThinkingSummary({
    reasoningStream: props.reasoningStream ?? null,
    stream: null,
    lastAssistantThinking,
  });
}
export function renderComposerStatusSlot(
  props: ChatProps,
  isBusy: boolean,
): import("lit").TemplateResult | typeof nothing {
  const sessionKey = props.sessionKey ?? "";
  const prevSticky = getComposerStickyForSession(sessionKey);
  // JAISIU-3752 — parallel sticky memo for the thinking summary so
  // the composer pill latches the most recent model message across
  // transient nulls in the live reasoning stream.
  const prevStickyThinking = getComposerStickyThinkingForSession(sessionKey);

  // When the run ends, drop the stickies so the next session starts
  // fresh. `isBusy` flipping false is the cleanest signal — it matches
  // the pinned bubble's chrome-went-away moment.
  if (!isBusy && (prevSticky !== null || prevStickyThinking !== null)) {
    clearComposerStickyForSession(sessionKey);
  }

  // 1) Live step — Thinking / Running / Heartbeat / etc.
  if (isBusy) {
    // Resolve through the same source of truth the pinned bubble uses so
    // the status slot and the pinned bubble cannot diverge. The resolver
    // returns `nextStickyLiveRunHint` even when no step renders (transient
    // out-of-band event) — memoize it to avoid surprise jumps.
    const resolved = resolveComposerLiveStep({
      runActive: true,
      toolMessageCount: props.toolMessages?.length ?? 0,
      hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
      streamText: props.stream ?? "",
      reasoningStreamText: props.reasoningStream ?? "",
      liveRunHint: props.liveRunHint ?? null,
      stickyLiveRunHint: prevSticky,
      compactionStatus: props.compactionStatus ?? null,
      subagents: sessionChildren(props),
      activeFileToolPath: props.chatActiveFileToolPath ?? null,
      thinkingSummary: resolveComposerThinkingSummary(props),
      stickyThinkingSummary: prevStickyThinking,
    });
    rememberComposerSticky(sessionKey, resolved.nextStickyLiveRunHint);
    rememberComposerStickyThinking(sessionKey, resolved.nextStickyThinkingSummary);
    if (resolved.step) {
      return renderComposerLiveStep({
        runActive: true,
        toolMessageCount: props.toolMessages?.length ?? 0,
        hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
        streamText: props.stream ?? "",
        reasoningStreamText: props.reasoningStream ?? "",
        liveRunHint: props.liveRunHint ?? null,
        stickyLiveRunHint: prevSticky,
        compactionStatus: props.compactionStatus ?? null,
        subagents: sessionChildren(props),
        activeFileToolPath: props.chatActiveFileToolPath ?? null,
        thinkingSummary: resolveComposerThinkingSummary(props),
        stickyThinkingSummary: prevStickyThinking,
      });
    }
    // No live step yet (run was just opened) — fall through to sync pill
    // so the operator still gets a status signal during the brief gap
    // before the first event lands.
  } else {
    // 2) Queued — per-session live snapshot + client-side parked queue.
    const queuedCount = Math.max(
      0,
      Math.floor(props.queue?.length ?? 0),
      Math.floor(props.collectQueueDepth ?? 0),
    );
    if (queuedCount > 0) {
      const queuedNode = renderComposerLiveStep({
        runActive: false,
        queuedCount,
        toolMessageCount: 0,
        hasActiveTool: false,
        streamText: "",
        compactionStatus: props.compactionStatus ?? null,
        subagents: sessionChildren(props),
      });
      if (queuedNode !== nothing) {
        return queuedNode;
      }
    }
  }

  // 3) Sync pill — Awaiting reply / Syncing / Disconnected.
  return renderChatSyncPill(
    {
      chatSync: props.chatSync ?? undefined,
      chatRunId: props.chatRunIdForPill ?? null,
      chatSending: props.chatSendingForPill === true,
      chatStreamStartedAt: props.chatStreamStartedAtForPill ?? null,
      connected: props.connected,
    },
    props.chatWaitNow ?? Date.now(),
  );
}

export function renderComposerFooterPill(
  props: ChatProps,
  isBusy: boolean,
): import("lit").TemplateResult | typeof nothing {
  const sessionKey = props.sessionKey ?? "";
  const prevSticky = getComposerStickyForSession(sessionKey);
  // JAISIU-3752 — parallel sticky memo for the thinking summary.
  const prevStickyThinking = getComposerStickyThinkingForSession(sessionKey);
  // JAISIU-chat-switch-reload — derive the queued count for this
  // session. We OR the two queues: the per-session live snapshot's
  // collect depth (server-side "queued behind an in-flight run") and
  // the client-side draft queue the operator parked while busy. The
  // pill renders whichever is bigger so the operator sees a single
  // coherent count, and it falls back to the standard sync / idle
  // render when both are zero.
  const queuedCount = Math.max(
    0,
    Math.floor(props.queue?.length ?? 0),
    Math.floor(props.collectQueueDepth ?? 0),
  );

  // When the run ends, drop the stickies so the next session starts
  // fresh. `isBusy` flipping false is the cleanest signal — it
  // matches the pinned bubble's chrome-went-away moment.
  if (!isBusy) {
    if (prevSticky !== null || prevStickyThinking !== null) {
      clearComposerStickyForSession(sessionKey);
    }
    // JAISIU-chat-switch-reload — render the `queued` pill BEFORE the
    // sync pill so the operator sees the parked state instead of an
    // "Awaiting reply · Ns" message they cannot act on. The resolver
    // returns `null` when `queuedCount === 0` so the standard sync /
    // idle path still owns the no-pill case.
    if (queuedCount > 0) {
      const queuedNode = renderComposerLiveStep({
        runActive: false,
        queuedCount,
        toolMessageCount: 0,
        hasActiveTool: false,
        streamText: "",
        compactionStatus: props.compactionStatus ?? null,
        subagents: sessionChildren(props),
      });
      if (queuedNode !== nothing) {
        return queuedNode;
      }
    }
    return renderChatSyncPill(
      {
        chatSync: props.chatSync ?? undefined,
        chatRunId: props.chatRunIdForPill ?? null,
        chatSending: props.chatSendingForPill === true,
        chatStreamStartedAt: props.chatStreamStartedAtForPill ?? null,
        connected: props.connected,
      },
      props.chatWaitNow ?? Date.now(),
    );
  }

  // Mirror the same step inputs the pinned bubble uses, so the composer
  // footer and the pinned bubble cannot diverge on what the model is
  // doing right now. Single-source-of-truth: buildLiveRunStepsWithSticky.
  // JAISIU-3752 — thinking summary is sourced from the same helper
  // the live bubble uses, so the two surfaces cannot diverge on what
  // the model is thinking about.
  const thinkingSummary = resolveComposerThinkingSummary(props);
  const step = resolveComposerLiveStep({
    runActive: isBusy,
    toolMessageCount: props.toolMessages?.length ?? 0,
    hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
    streamText: props.stream ?? "",
    reasoningStreamText: props.reasoningStream ?? "",
    liveRunHint: props.liveRunHint ?? null,
    stickyLiveRunHint: prevSticky,
    compactionStatus: props.compactionStatus ?? null,
    subagents: sessionChildren(props),
    activeFileToolPath: props.chatActiveFileToolPath ?? null,
    thinkingSummary,
    stickyThinkingSummary: prevStickyThinking,
  });
  // Latch the new stickies even when we end up rendering nothing — the
  // gateway hint may have arrived but produced no step (e.g. transient
  // out-of-band event). Keeping the memos in lockstep with the pinned
  // bubble's memo (chat-assistant-turn.ts) avoids surprise jumps.
  rememberComposerSticky(sessionKey, step.nextStickyLiveRunHint);
  rememberComposerStickyThinking(sessionKey, step.nextStickyThinkingSummary);

  const liveNode = renderComposerLiveStep({
    runActive: isBusy,
    toolMessageCount: props.toolMessages?.length ?? 0,
    hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
    streamText: props.stream ?? "",
    reasoningStreamText: props.reasoningStream ?? "",
    liveRunHint: props.liveRunHint ?? null,
    stickyLiveRunHint: prevSticky,
    compactionStatus: props.compactionStatus ?? null,
    subagents: sessionChildren(props),
    activeFileToolPath: props.chatActiveFileToolPath ?? null,
    thinkingSummary,
    stickyThinkingSummary: prevStickyThinking,
  });
  // JAISIU-3588 — the live-step pill (Thinking… / Running / etc.)
  // is now rendered in the tools row next to the heartbeat, not in
  // the dock footer. The dock footer only carries queued + sync
  // pills (status signals that the operator reads when the model
  // is NOT actively working). Returning `nothing` here keeps the
  // footer empty when there is nothing to report, so the textarea
  // can stretch upward and the gap above/below the tools row can
  // be evenly spaced.
  // No live step yet (run was just opened) — fall back to staleness pill
  // so the operator still gets a status signal during the brief gap
  // before the first event lands.
  return renderChatSyncPill(
    {
      chatSync: props.chatSync ?? undefined,
      chatRunId: props.chatRunIdForPill ?? null,
      chatSending: props.chatSendingForPill === true,
      chatStreamStartedAt: props.chatStreamStartedAtForPill ?? null,
      connected: props.connected,
    },
    props.chatWaitNow ?? Date.now(),
  );
}

/** Dock footer under the unified / float composer (latency, stop, new session). */
export function renderChatFleetDockFooter(
  props: ChatProps,
  opts: { canAbort: boolean; isBusy: boolean; onScrollFleetQueue: () => void },
): import("lit").TemplateResult {
  return html`
    <div class="chat-fleet-footer">
      <div class="chat-fleet-footer__left">${renderComposerFooterPill(props, opts.isBusy)}</div>
      <div class="chat-fleet-footer__right">
        <span
          class="chat-fleet-footer__latency ${props.connected ? "" : "chat-fleet-footer__latency--offline"}"
        >
          <span class="chat-fleet-footer__latency-dot" aria-hidden="true"></span>
          ${props.connected ? "Online" : "Offline"}
        </span>
        ${
          opts.canAbort
            ? html`
                <button
                  type="button"
                  class="chat-fleet-footer__btn chat-fleet-footer__btn--stop"
                  title="Stop the current agent run"
                  @click=${() => props.onAbort?.()}
                >
                  Stop run
                </button>
              `
            : nothing
        }
        ${
          props.onNewSession && !opts.canAbort
            ? html`
                <button
                  type="button"
                  class="chat-fleet-footer__btn"
                  title="Start a new chat session"
                  @click=${props.onNewSession}
                >
                  New session
                </button>
              `
            : nothing
        }
        <button
          type="button"
          class="chat-fleet-footer__btn"
          title="Scroll the message list to the fleet orchestration queue."
          @click=${opts.onScrollFleetQueue}
          aria-label="Scroll to fleet queue"
        >
          ${icons.list} Fleet queue
        </button>
      </div>
    </div>
  `;
}

export function renderComposeWaitStatus(props: ChatProps, isBusy: boolean) {
  if (
    !shouldShowLiveRunIndicator({
      sending: props.sending,
      canAbort: props.canAbort,
      stream: props.stream,
      streamStartedAt: props.streamStartedAt,
      nowMs: props.chatWaitNow ?? Date.now(),
    })
  ) {
    return nothing;
  }
  const startedAt = props.streamStartedAt;
  if (startedAt == null) {
    return nothing;
  }
  const elapsed = formatChatWaitElapsed(startedAt, props.chatWaitNow ?? Date.now());
  return html`
    <p
      class="chat-compose__run-status"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="agent-busy"
    >
      <span class="chat-wait-elapsed" title="Time waiting for the agent reply">${elapsed}</span>
      <span class="chat-compose__run-status-text">
        waiting for answer — queue a follow-up or stop (Esc)
      </span>
    </p>
  `;
}

export function adjustTextareaHeight(el: HTMLTextAreaElement) {
  // Clear inline minHeight from a previous growth so scrollHeight measures the
  // natural content height. Without this, after a tall paste the inline
  // minHeight (e.g. "320px") floors scrollHeight to that value, so clearing
  // the textarea content never shrinks the element back to its normal size.
  el.style.height = "auto";
  el.style.minHeight = "";
  const contentHeight = el.scrollHeight;
  if (el.closest(".chat-compose-float-dock") || el.closest(".chat-compose-inline-dock")) {
    // JAISIU-3588 (followup) — compact composer: empty = ~2 lines
    // (minPx 60), grows smoothly up to ~11 lines (maxPx 280).
    // After that, the textarea scrolls inside its own box (the
    // shell height stays fixed so the dock doesn't bounce the
    // transcript). The cap is exact (not 55vh) so the size is
    // stable across viewport heights.
    //
    // floor-only minHeight: never raise it with contentHeight, or
    // min-height can beat max-height and the flex transcript
    // sibling jitters. The post-send shrink in
    // handleComposerSend re-runs this helper, which clears
    // minHeight first so a tall paste does not floor the empty
    // state on the next render.
    const minPx = 60;
    const maxPx = 280;
    const next = Math.min(Math.max(contentHeight, minPx), maxPx);
    el.style.maxHeight = `${maxPx}px`;
    el.style.height = `${next}px`;
    el.style.minHeight = `${minPx}px`;
    return;
  }
  if (el.closest(".fleet-v2-panel--send")) {
    const minPx = 80;
    const maxPx = 280;
    const next = Math.min(Math.max(contentHeight, minPx), maxPx);
    el.style.maxHeight = "280px";
    el.style.height = `${next}px`;
    el.style.minHeight = `${minPx}px`;
    return;
  }
  // Default compose textarea
  el.style.maxHeight = "150px";
  el.style.height = `${Math.min(contentHeight, 150)}px`;
}

export function renderCompactionIndicator(status: CompactionIndicatorStatus | null | undefined) {
  if (!status) {
    return nothing;
  }

  // Show "compacting..." while active
  if (status.active) {
    return html`
      <div class="callout info compaction-indicator compaction-indicator--active">
        ${icons.loader} Compacting context...
      </div>
    `;
  }

  // Show "compaction complete" briefly after completion
  if (status.completedAt) {
    const elapsed = Date.now() - status.completedAt;
    if (elapsed < COMPACTION_TOAST_DURATION_MS) {
      return html`
        <div class="callout success compaction-indicator compaction-indicator--complete">
          ${icons.check} Context compacted
        </div>
      `;
    }
  }

  return nothing;
}

export {
  appendMentionOrSlashTrigger,
  flushComposeDraftFromDom,
  readComposeTextareaValue,
  stripTrailingSlashOrMentionToken,
  syncChatComposeDraft,
} from "./chat-composer-draft.ts";

export {
  attachmentsReady,
  handlePaste,
  isAttachmentStored,
  readSelectionText,
  renderAttachmentPreview,
} from "./chat-composer-attachments.ts";
